import React from "react";
import Select from "react-select";
import { getSelectStyles } from "../utils/utils";
import { renderSelectionChipRow, selectionChipListStyle } from "../utils/selection-chip-styles";

/**
 * XPEN-3494: the shared Departments / Roles / Approvers picker.
 *
 * Chosen entries never render inside the select box — `controlShouldRenderValue`
 * is false — they render beneath it as chip rows from
 * `src/utils/selection-chip-styles.jsx`, each with its own remove button. That
 * matters beyond looks: `getSelectStyles` hard-sets the control to 30px and it
 * cannot grow, so anything rendered inside it stops fitting after a few entries.
 *
 * **This is a factory, not a component.** It returns two separate nodes —
 * `control` and `selectionList` — and every caller places them itself. Six
 * placement sites rely on that: `renderModalField` puts helper text *between*
 * them for the Roles field, and the three subcategory pickers plus the two
 * Mileage visibility fields lay them out in their own markup. Collapsing this into one component that renders both together would
 * break all six, and the helper-text ordering silently.
 *
 * @param {string}   inputId       - Select input id; also what tests address it by.
 * @param {Array}    selectedIds   - Currently chosen ids. Non-arrays are treated as empty.
 * @param {Function} onChangeIds   - (nextIds) => void. Receives the full next list.
 * @param {Array}    options       - [{value, label}] to choose from.
 * @param {Function} resolveLabel  - (id) => string, for the chip rows.
 * @param {string}   placeholder   - Select placeholder.
 * @param {string}   chipTestId    - data-testid on the chip list.
 * @param {boolean}  editAllowed   - false disables the select and hides remove buttons.
 * @param {string}   removeLabel   - Text on each chip's remove button.
 * @returns {{control: JSX.Element, selectionList: JSX.Element|null}}
 */
export function buildMultiSelectPickerParts({
  inputId,
  selectedIds,
  onChangeIds,
  options,
  resolveLabel,
  placeholder,
  chipTestId,
  editAllowed,
  removeLabel,
}) {
  const ids = Array.isArray(selectedIds) ? selectedIds : [];
  return {
    control: (
      <Select
        inputId={inputId}
        classNamePrefix="select"
        isMulti
        controlShouldRenderValue={false}
        isDisabled={!editAllowed}
        value={options.filter((o) => ids.includes(o.value))}
        options={options.filter((o) => !ids.includes(o.value))}
        onChange={(selected) => onChangeIds((selected || []).map((s) => s.value))}
        styles={getSelectStyles(false, false)}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        placeholder={placeholder}
      />
    ),
    selectionList:
      ids.length > 0 ? (
        <div style={selectionChipListStyle()} data-testid={chipTestId}>
          {ids.map((id) =>
            renderSelectionChipRow({
              key: id,
              label: resolveLabel(id),
              canEdit: editAllowed,
              onRemove: () => onChangeIds(ids.filter((x) => x !== id)),
              removeLabel,
            })
          )}
        </div>
      ) : null,
  };
}

